import { AlertTriangle, Info, TriangleAlert } from "lucide-react";
import { m, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

interface ConfirmModalProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning" | "info";
  /** Receives the typed reason when `requireReason` is set. */
  onConfirm: (reason?: string) => Promise<void> | void;
  onCancel: () => void;
  children?: ReactNode;
  /** Asks for a reason (kept on the record's audit trail); Confirm stays disabled until one is written. */
  requireReason?: boolean | { label?: string; placeholder?: string };
  // When set, the Confirm button stays disabled until the user types this
  // exact word into a text field first (matches AWS console's "type delete
  // to confirm" pattern) — an extra speed bump for genuinely destructive,
  // hard-to-undo actions.
  requireTypedConfirmation?: string;
  /** Keeps Confirm disabled, e.g. until a required choice in `description` is made. */
  confirmDisabled?: boolean;
}

export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = "danger",
  onConfirm,
  onCancel,
  children,
  requireTypedConfirmation,
  requireReason,
  confirmDisabled,
}: ConfirmModalProps) {
  /* ================= LOCK BODY SCROLL ================= */

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  /* ================= ESC CLOSE ================= */

  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onCancel]);

  if (typeof window === "undefined") return null;

  // "danger" and "warning" stay semantic — red and amber mean the same thing
  // in any theme. "info" does not carry a meaning of its own: it is the
  // ordinary, affirmative confirm, so it takes the app's accent. Hardcoded
  // blue made these dialogs the only surface in the app that ignored the
  // theme, sitting in a teal UI with a blue header and a blue button.
  const color =
    variant === "danger"
      ? "bg-red-500/10 text-red-600 dark:text-red-400"
      : variant === "warning"
        ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
        : "bg-[rgb(var(--text-accent))]/10 text-[rgb(var(--text-accent))]";

  return createPortal(
    <AnimatePresence>
      {open && (
        <m.div
          className="fixed inset-0 z-[100000] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* BACKDROP */}
          <m.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCancel}
          />

          {/* MODAL — a separate component so its typed-confirmation/loading
              state is scoped to this mount and starts fresh every time the
              modal opens, instead of persisting on the parent across opens. */}
          <ConfirmModalPanel
            title={title}
            description={description}
            confirmLabel={confirmLabel}
            cancelLabel={cancelLabel}
            variant={variant}
            color={color}
            onConfirm={onConfirm}
            onCancel={onCancel}
            requireTypedConfirmation={requireTypedConfirmation}
            requireReason={requireReason}
            confirmDisabled={confirmDisabled}
          >
            {children}
          </ConfirmModalPanel>
        </m.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

interface ConfirmModalPanelProps {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant: "danger" | "warning" | "info";
  color: string;
  onConfirm: (reason?: string) => Promise<void> | void;
  onCancel: () => void;
  children?: ReactNode;
  requireTypedConfirmation?: string;
  requireReason?: ConfirmModalProps["requireReason"];
  confirmDisabled?: boolean;
}

function ConfirmModalPanel({
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant,
  color,
  onConfirm,
  onCancel,
  children,
  requireTypedConfirmation,
  requireReason,
  confirmDisabled,
}: ConfirmModalPanelProps) {
  const [loading, setLoading] = useState(false);
  const [typedValue, setTypedValue] = useState("");
  const [reason, setReason] = useState("");
  // Shown when Confirm is pressed without a reason: the button stays pressable so the person learns why.
  const [reasonError, setReasonError] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const reasonOptions = typeof requireReason === "object" ? requireReason : {};
  const { t } = useTranslation(["common"]);

  const VariantIcon =
    variant === "danger"
      ? TriangleAlert
      : variant === "warning"
        ? AlertTriangle
        : Info;

  const activeConfirmLabel = confirmLabel || t("common:actions.confirm");
  const activeCancelLabel = cancelLabel || t("common:actions.cancel");
  // Forgiving about case and stray spaces ("delete", "DELETE "): the word is
  // a deliberate pause, not a password, and a silent mismatch on a trailing
  // space read as "delete doesn't work".
  const typedConfirmationMet =
    !requireTypedConfirmation ||
    typedValue.trim().toUpperCase() === requireTypedConfirmation.trim().toUpperCase();
  const reasonMet = !requireReason || reason.trim().length > 0;

  return (
    <m.div
      initial={{ scale: 0.94, y: 24, opacity: 0 }}
      animate={{ scale: 1, y: 0, opacity: 1 }}
      exit={{ scale: 0.96, y: 12, opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="
        relative z-10
        mx-4 w-full max-w-md
        overflow-hidden rounded-2xl
        border border-[rgb(var(--border))]
        bg-[rgb(var(--card))]
        shadow-2xl
      "
      role="dialog"
      aria-modal="true"
    >
      {/* HEADER — a tinted icon tile and the title on the card surface, as the
          other dialogs now have. Severity lives in the tile (icon shape plus
          hue, so it does not rely on colour alone) and in the confirm button. */}
      <div className="flex items-center gap-3 px-6 pt-5">
        <span
          aria-hidden="true"
          className={clsx(
            "grid h-10 w-10 shrink-0 place-items-center rounded-xl",
            color,
          )}
        >
          <VariantIcon size={20} />
        </span>
        <h2 className="min-w-0 text-lg font-semibold leading-snug text-[rgb(var(--text-primary))]">
          {title}
        </h2>
      </div>

      {/* BODY */}
      <div className="px-6 pb-5 pt-3">
        <div className="text-sm leading-relaxed text-[rgb(var(--text-secondary))]">
          {description}
          {children}
        </div>

        {requireReason && (
          <div className="mt-4">
            <label htmlFor="confirm-modal-reason" className="mb-1 block text-sm text-[rgb(var(--text-muted))]">
              {reasonOptions.label ?? t("common:modal.reason", { defaultValue: "Reason" })}
              <span className="text-red-600"> *</span>
            </label>
            <textarea
              id="confirm-modal-reason"
              autoFocus
              rows={3}
              maxLength={1000}
              ref={reasonRef}
              value={reason}
              aria-invalid={reasonError || undefined}
              aria-describedby={reasonError ? "confirm-modal-reason-error" : undefined}
              onChange={(e) => {
                setReason(e.target.value);
                if (e.target.value.trim()) setReasonError(false);
              }}
              placeholder={reasonOptions.placeholder ?? t("common:modal.reasonPlaceholder", { defaultValue: "Why is this being deleted?" })}
              className="
                w-full resize-y rounded-lg px-3 py-2 text-sm
                border border-[rgb(var(--border))]
                bg-[rgb(var(--card-muted))]
                text-[rgb(var(--text-primary))]
                focus:outline-none focus:ring-2
                focus:ring-[rgb(var(--text-accent))]
              "
            />
            {reasonError && (
              <p id="confirm-modal-reason-error" role="alert" className="mt-1 text-xs font-medium text-red-600">
                {t("common:modal.reasonRequired", { defaultValue: "Enter the reason." })}
              </p>
            )}
          </div>
        )}

        {requireTypedConfirmation && (
          <div className="mt-4">
            <label className="block text-sm text-[rgb(var(--text-muted))] mb-1">
              {t("common:modal.typeToConfirm", {
                defaultValue: `To confirm, type "{{word}}" below.`,
                word: requireTypedConfirmation,
              })}
            </label>
            <input
              type="text"
              autoFocus={!requireReason}
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder={requireTypedConfirmation}
              className="
                w-full px-3 py-2 rounded-lg text-sm
                border border-[rgb(var(--border))]
                bg-[rgb(var(--card-muted))]
                text-[rgb(var(--text-primary))]
                focus:outline-none focus:ring-2
                focus:ring-[rgb(var(--text-accent))]
              "
            />
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div className="flex justify-end gap-3 border-t border-[rgb(var(--border))] bg-[rgb(var(--card-muted))] px-6 py-4">
        <button
          onClick={onCancel}
          disabled={loading}
          className="
            rounded-lg px-4 py-2 text-sm
            border border-[rgb(var(--border))]
            bg-[rgb(var(--card))]
            hover:bg-[rgb(var(--card-hover))]
            text-[rgb(var(--text-primary))]
            transition-colors
            focus:outline-none focus-visible:ring-2
            focus-visible:ring-[rgb(var(--text-accent))]
          "
        >
          {activeCancelLabel}
        </button>

        <button
          onClick={async () => {
            if (!reasonMet) {
              setReasonError(true);
              reasonRef.current?.focus();
              return;
            }
            try {
              setLoading(true);
              await onConfirm(requireReason ? reason.trim() : undefined);
            } finally {
              setLoading(false);
            }
          }}
          disabled={loading || !typedConfirmationMet || confirmDisabled}
          className={clsx(
            "rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm transition",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
            "focus-visible:ring-[rgb(var(--text-accent))] focus-visible:ring-offset-[rgb(var(--card-muted))]",
            (!typedConfirmationMet || confirmDisabled) && "opacity-50 cursor-not-allowed",
            variant === "danger"
              ? "bg-red-600 hover:bg-red-700"
              : variant === "warning"
                ? "bg-amber-600 hover:bg-amber-700"
                : "bg-[rgb(var(--accent-fill))] hover:opacity-90",
          )}
        >
          {loading ? t("common:modal.pleaseWait") : activeConfirmLabel}
        </button>
      </div>
    </m.div>
  );
}
